import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import notificationsService from '../../../services/notificationsService';

const NotificationComposer = ({ onNotificationCreated, artistId }) => {
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'general',
    scheduledFor: '',
    targetAudience: 'all'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const notificationTypes = [
    { value: 'general', label: 'General', icon: 'MessageCircle', description: 'General announcements' },
    { value: 'event', label: 'Event', icon: 'Calendar', description: 'Event notifications' },
    { value: 'release', label: 'Release', icon: 'Music', description: 'New music releases' },
    { value: 'merchandise', label: 'Merchandise', icon: 'ShoppingBag', description: 'Shop updates' }
  ];

  const audienceOptions = [
    { value: 'all', label: 'All Fans', description: 'Send to all app users' },
    { value: 'active', label: 'Active Users', description: 'Users active in last 30 days' },
    { value: 'subscribers', label: 'Subscribers', description: 'Premium subscribers only' },
    { value: 'location', label: 'By Location', description: 'Target specific regions' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const validateForm = () => {
    if (!formData?.title?.trim()) {
      setError('Title is required');
      return false;
    }
    if (!formData?.message?.trim()) {
      setError('Message is required');
      return false;
    }
    if (formData?.title?.length > 50) {
      setError('Title must be 50 characters or less');
      return false;
    }
    if (formData?.message?.length > 200) {
      setError('Message must be 200 characters or less');
      return false;
    }
    return true;
  };

  const handleSaveDraft = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError(null);

    try {
      const notificationData = {
        artistId,
        title: formData?.title?.trim(),
        message: formData?.message?.trim(),
        type: formData?.type,
        status: 'draft',
        targetAudience: { type: formData?.targetAudience }
      };

      const result = await notificationsService?.createNotification(notificationData);
      if (result?.error) throw new Error(result.error);

      setSuccess('Notification saved as draft');
      onNotificationCreated?.(result?.data);
      
      // Reset form
      setFormData({
        title: '',
        message: '',
        type: 'general',
        scheduledFor: '',
        targetAudience: 'all'
      });
    } catch (err) {
      setError(err?.message || 'Failed to save notification');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError(null);

    try {
      // First create the notification
      const notificationData = {
        artistId,
        title: formData?.title?.trim(),
        message: formData?.message?.trim(),
        type: formData?.type,
        status: 'draft',
        targetAudience: { type: formData?.targetAudience }
      };

      const createResult = await notificationsService?.createNotification(notificationData);
      if (createResult?.error) throw new Error(createResult.error);

      // Then send it immediately
      const sendResult = await notificationsService?.sendNotification(createResult?.data?.id);
      if (sendResult?.error) throw new Error(sendResult.error);

      setSuccess('Notification sent successfully!');
      onNotificationCreated?.(sendResult?.data);
      
      // Reset form
      setFormData({
        title: '',
        message: '',
        type: 'general',
        scheduledFor: '',
        targetAudience: 'all'
      });
    } catch (err) {
      setError(err?.message || 'Failed to send notification');
    } finally {
      setSaving(false);
    }
  };

  const handleSchedule = async () => {
    if (!validateForm()) return;
    if (!formData?.scheduledFor) {
      setError('Please select a schedule date and time');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const notificationData = {
        artistId,
        title: formData?.title?.trim(),
        message: formData?.message?.trim(),
        type: formData?.type,
        status: 'scheduled',
        scheduledFor: new Date(formData?.scheduledFor)?.toISOString(),
        targetAudience: { type: formData?.targetAudience }
      };

      const result = await notificationsService?.createNotification(notificationData);
      if (result?.error) throw new Error(result.error);

      setSuccess('Notification scheduled successfully');
      onNotificationCreated?.(result?.data);
      
      // Reset form
      setFormData({
        title: '',
        message: '',
        type: 'general',
        scheduledFor: '',
        targetAudience: 'all'
      });
    } catch (err) {
      setError(err?.message || 'Failed to schedule notification');
    } finally {
      setSaving(false);
    }
  };

  // Get minimum datetime for scheduling (current time + 5 minutes)
  const getMinDateTime = () => {
    const now = new Date();
    now?.setMinutes(now?.getMinutes() + 5);
    return now?.toISOString()?.slice(0, 16);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-text-primary mb-2">Create Notification</h2>
        <p className="text-sm text-text-secondary">
          Compose a message to send to your mobile app users
        </p>
      </div>
      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} className="text-red-600" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="CheckCircle" size={16} className="text-green-600" />
            <p className="text-sm text-green-800">{success}</p>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Compose Form */}
        <div className="space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Notification Title
            </label>
            <Input
              value={formData?.title}
              onChange={(e) => handleInputChange('title', e?.target?.value)}
              placeholder="Enter notification title..."
              maxLength={50}
            />
            <div className="mt-1 flex justify-between text-xs text-text-secondary">
              <span>Keep it short and compelling</span>
              <span>{formData?.title?.length || 0}/50</span>
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Message
            </label>
            <textarea
              value={formData?.message}
              onChange={(e) => handleInputChange('message', e?.target?.value)}
              placeholder="Write your notification message..."
              rows={4}
              maxLength={200}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent resize-none bg-surface text-text-primary placeholder:text-text-secondary"
            />
            <div className="mt-1 flex justify-between text-xs text-text-secondary">
              <span>Clear and actionable message</span>
              <span>{formData?.message?.length || 0}/200</span>
            </div>
          </div>

          {/* Notification Type */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              {notificationTypes?.map((type) => (
                <label
                  key={type?.value}
                  className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                    formData?.type === type?.value
                      ? 'border-accent bg-accent/5 text-accent' :'border-border hover:border-accent/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="type"
                    value={type?.value}
                    checked={formData?.type === type?.value}
                    onChange={(e) => handleInputChange('type', e?.target?.value)}
                    className="sr-only"
                  />
                  <div className="flex items-center space-x-2">
                    <Icon name={type?.icon} size={16} />
                    <div>
                      <div className="text-sm font-medium">{type?.label}</div>
                      <div className="text-xs text-text-secondary">{type?.description}</div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Target Audience */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Target Audience
            </label>
            <select
              value={formData?.targetAudience}
              onChange={(e) => handleInputChange('targetAudience', e?.target?.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
            >
              {audienceOptions?.map((option) => (
                <option key={option?.value} value={option?.value}>
                  {option?.label} - {option?.description}
                </option>
              ))}
            </select>
          </div>

          {/* Schedule */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Schedule (Optional)
            </label>
            <Input
              type="datetime-local"
              value={formData?.scheduledFor}
              onChange={(e) => handleInputChange('scheduledFor', e?.target?.value)}
              min={getMinDateTime()}
            />
            <p className="mt-1 text-xs text-text-secondary">
              Leave empty to send immediately or schedule for later
            </p>
          </div>
        </div>

        {/* Preview */}
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-4">Preview</h3>
            
            {/* Mobile Preview */}
            <div className="bg-gray-900 rounded-lg p-4">
              <div className="bg-surface rounded-lg shadow-sm border border-border p-4 max-w-sm mx-auto">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                    <Icon name="Music" size={16} color="white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text-primary truncate">
                      {formData?.title || 'Notification Title'}
                    </div>
                    <div className="text-sm text-text-secondary mt-1 line-clamp-2">
                      {formData?.message || 'Your notification message will appear here...'}
                    </div>
                    <div className="text-xs text-text-secondary mt-2">
                      {new Date()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              variant="default"
              onClick={handleSendNow}
              disabled={saving || !formData?.title || !formData?.message}
              iconName="Send"
              iconPosition="left"
              iconSize={16}
              className="w-full"
            >
              {saving ? 'Sending...' : 'Send Now'}
            </Button>

            <Button
              variant="outline"
              onClick={handleSchedule}
              disabled={saving || !formData?.title || !formData?.message || !formData?.scheduledFor}
              iconName="Calendar"
              iconPosition="left"
              iconSize={16}
              className="w-full"
            >
              Schedule
            </Button>

            <Button
              variant="ghost"
              onClick={handleSaveDraft}
              disabled={saving || !formData?.title || !formData?.message}
              iconName="Save"
              iconPosition="left"
              iconSize={16}
              className="w-full"
            >
              Save Draft
            </Button>
          </div>

          {/* Tips */}
          <div className="bg-blue-950/50 border border-blue-900/40 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-300 mb-2">
              <Icon name="Lightbulb" size={16} className="inline mr-1" />
              Tips for Better Engagement
            </h4>
            <ul className="text-xs text-blue-200 space-y-1">
              <li>• Keep titles under 40 characters for better visibility</li>
              <li>• Use action words like "New", "Live", "Limited"</li>
              <li>• Personalize when possible</li>
              <li>• Send at optimal times (6-8 PM is often best)</li>
              <li>• Test different message formats</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationComposer;